import { useEffect, useRef, useState } from 'react'
import type { Submission } from '@/types'
import { Modal } from '@/components/ui/Modal/Modal'
import { useToast } from '@/components/providers/ToastProvider'
import { getDisplayDate } from '@/components/RecordingItem/formatters'
import { updateTranscript } from '@/lib/api'

interface TranscriptModalProps {
  isOpen: boolean
  onClose: () => void
  submissionId: string
  transcript: string
  transcriptEditedAt?: string | null
  title: string
  createdAt: string
  /** Called with the server's updated submission after a successful save. */
  onSaved: (updated: Submission) => void
  /** When false, the transcript is view-only (no Edit button). Used once the
   *  asset's active period closes — editing is also rejected server-side. */
  canEdit?: boolean
}

// Generous client cap; the server enforces the authoritative byte limit.
const MAX_TRANSCRIPT_CHARACTERS = 50_000

const PRIMARY_BUTTON =
  'px-4 py-2 rounded-lg bg-neon-cyan text-navy-dark font-semibold hover:bg-neon-cyan/80 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed'
const SECONDARY_BUTTON =
  'px-4 py-2 rounded-lg bg-gray-600/20 text-gray-300 hover:bg-gray-600/30 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * Shows a recording's transcript and lets the owner correct it. Editing is
 * online-only (the transcript lives server-side); on save it calls the API and
 * hands the updated submission back via `onSaved`.
 */
export function TranscriptModal({
  isOpen,
  onClose,
  submissionId,
  transcript,
  transcriptEditedAt,
  title,
  createdAt,
  onSaved,
  canEdit = true,
}: TranscriptModalProps) {
  const { showToast } = useToast()
  // `value`/`editedAt` are the saved state shown in view mode; `draft` is the
  // edit buffer. Both reset whenever the modal (re)opens.
  const [value, setValue] = useState(transcript)
  const [editedAt, setEditedAt] = useState<string | null>(transcriptEditedAt ?? null)
  const [draft, setDraft] = useState(transcript)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setValue(transcript)
    setEditedAt(transcriptEditedAt ?? null)
    setDraft(transcript)
    setIsEditing(false)
    setIsSaving(false)
  }, [isOpen, transcript, transcriptEditedAt])

  useEffect(() => {
    if (isEditing) textareaRef.current?.focus()
  }, [isEditing])

  const trimmed = draft.trim()
  const isEmpty = trimmed.length === 0
  const isUnchanged = trimmed === value.trim()
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
  const canSave = !isSaving && !isEmpty && !isUnchanged && !isOffline

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const handleSave = async () => {
    if (!canSave) {
      if (isOffline) showToast('You are offline. Reconnect to save changes.', 'warning')
      else if (isEmpty) showToast('Transcript cannot be empty', 'warning')
      return
    }
    setIsSaving(true)
    try {
      const updated = await updateTranscript(submissionId, trimmed)
      setValue(updated.transcript ?? trimmed)
      setEditedAt(updated.transcriptEditedAt ?? null)
      setIsEditing(false)
      onSaved(updated)
      showToast('Transcript updated', 'success')
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update transcript',
        'error',
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value
    if (next.length <= MAX_TRANSCRIPT_CHARACTERS) setDraft(next)
  }

  const footer = isEditing ? (
    <>
      <button
        type="button"
        onClick={() => {
          setDraft(value)
          setIsEditing(false)
        }}
        disabled={isSaving}
        className={SECONDARY_BUTTON}
      >
        Cancel
      </button>
      <button type="button" onClick={handleSave} disabled={!canSave} className={PRIMARY_BUTTON}>
        {isSaving ? 'Saving…' : 'Save'}
      </button>
    </>
  ) : (
    <>
      {canEdit && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className={PRIMARY_BUTTON}
        >
          Edit
        </button>
      )}
      <button type="button" onClick={handleClose} className={SECONDARY_BUTTON}>
        Close
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={createdAt}
      footer={footer}
    >
      {isEditing ? (
        <div>
          <label htmlFor="transcript-editor" className="sr-only">
            Edit transcript
          </label>
          <textarea
            id="transcript-editor"
            ref={textareaRef}
            value={draft}
            onChange={handleChange}
            rows={12}
            maxLength={MAX_TRANSCRIPT_CHARACTERS}
            aria-describedby="transcript-counter"
            className="w-full rounded-lg bg-navy-dark border border-navy-light p-3 text-sm text-gray-200 leading-relaxed focus:outline-none focus:ring-2 focus:ring-neon-cyan"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            {isOffline ? (
              <span className="text-xs text-yellow-400" role="alert">
                Offline — reconnect to save.
              </span>
            ) : (
              <span />
            )}
            <span
              id="transcript-counter"
              className={`text-xs ${
                MAX_TRANSCRIPT_CHARACTERS - draft.length < 500 ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              {draft.length}/{MAX_TRANSCRIPT_CHARACTERS}
            </span>
          </div>
        </div>
      ) : (
        <div>
          {editedAt && (
            <p className="mb-2 text-xs italic text-gray-400">Edited · {getDisplayDate(editedAt)}</p>
          )}
          <p className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed">{value}</p>
        </div>
      )}
    </Modal>
  )
}
